// Klaviyo public API key (Site ID) — safe to expose client-side. The list ID
// is configured per-block via the "Klaviyo list ID" theme setting instead.
const KLAVIYO_PUBLIC_API_KEY = 'XLfaDv';

if (!customElements.get('notify-me-form')) {
  customElements.define(
    'notify-me-form',
    class NotifyMeForm extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.form = this.querySelector('form');
        this.emailInput = this.querySelector('[name="email"]');
        this.variantIdInput = this.querySelector('[name="variant_id"]');
        this.variantTitleInput = this.querySelector('[name="variant_title"]');
        this.submitBtn = this.querySelector('[type="submit"]');
        this.successEl = this.querySelector('[data-status="success"]');
        this.errorEl = this.querySelector('[data-status="error"]');
        this.errorTextEl = this.querySelector('.notify-me-form__error-text');
        this.defaultErrorText = this.errorTextEl ? this.errorTextEl.textContent : '';

        // Redesign (Oct 2026): the thumbnail and the pre-order shortcut follow the selected variant.
        this.thumbEl = this.querySelector('[data-notify-thumb]');
        this.preorderRow = this.querySelector('[data-notify-preorder-row]');
        this.preorderBtn = this.querySelector('[data-notify-preorder]');
        this.variantMap = {};
        try {
          const json = this.querySelector('[data-notify-variants]');
          if (json) this.variantMap = JSON.parse(json.textContent);
        } catch (e) {
          console.error('[NotifyMeForm] variant data parse failed:', e);
        }
        this.dialog = this.closest('modal-dialog');
        if (this.dialog) {
          // theme.js updates the hidden variant inputs on variant change, so re-read them each time the popup opens.
          new MutationObserver(() => {
            if (this.dialog.hasAttribute('open')) this.syncVariant();
          }).observe(this.dialog, { attributes: true, attributeFilter: ['open'] });
        }
        this.syncVariant();
        if (this.preorderBtn) this.preorderBtn.addEventListener('click', this.onPreorder.bind(this));

        this.form.addEventListener('submit', this.onSubmit.bind(this));
      }

      syncVariant() {
        const variant = this.variantMap[String(this.variantIdInput ? this.variantIdInput.value : '')];
        if (this.thumbEl && variant && variant.img) this.thumbEl.src = variant.img;
        if (this.preorderRow) this.preorderRow.hidden = !(variant && variant.preorder);
      }

      // Closes the popup and presses the product's own buy button, so the normal
      // pre-order flow (insole offer, cart drawer) runs for the selected size.
      onPreorder() {
        if (this.dialog && typeof this.dialog.hide === 'function') this.dialog.hide();
        const buttons = [...document.querySelectorAll('product-form form')]
          .filter((form) => {
            const input = form.querySelector('[name="product-id"]');
            return input && input.value === this.dataset.productId;
          })
          .map((form) => form.querySelector('[type="submit"]'))
          .filter((button) => button && !button.disabled);
        const button = buttons.find((b) => b.offsetParent !== null) || buttons[0];
        if (button) button.click();
      }

      hideStatus() {
        this.successEl?.setAttribute('hidden', '');
        this.errorEl?.setAttribute('hidden', '');
      }

      showStatus(kind, message) {
        this.hideStatus();
        if (kind === 'success') {
          this.successEl?.removeAttribute('hidden');
        } else {
          if (this.errorTextEl) this.errorTextEl.textContent = message || this.defaultErrorText;
          this.errorEl?.removeAttribute('hidden');
        }
      }

      // Subscribes the shopper to the Klaviyo list via Klaviyo's public
      // "Client Subscribe" API — safe to call with only the public API key.
      // Fire-and-forget: a failure here (e.g. blocked by an ad blocker)
      // shouldn't stop the shopper from seeing the request was recorded.
      async subscribeToKlaviyoList(email) {
        const listId = this.dataset.klaviyoListId;
        if (!listId) return; // Not configured for this block — event tracking still happened.

        try {
          const response = await fetch(
            `https://a.klaviyo.com/client/subscriptions/?company_id=${KLAVIYO_PUBLIC_API_KEY}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                revision: '2024-10-15',
              },
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    profile: {
                      data: {
                        type: 'profile',
                        attributes: { email },
                      },
                    },
                  },
                  relationships: {
                    list: {
                      data: { type: 'list', id: listId },
                    },
                  },
                },
              }),
            }
          );
          if (!response.ok) {
            console.error('[NotifyMeForm] Klaviyo list subscribe failed:', response.status, await response.text());
          }
        } catch (e) {
          console.error('[NotifyMeForm] Klaviyo list subscribe failed:', e);
        }
      }

      async onSubmit(evt) {
        evt.preventDefault();
        this.hideStatus();

        if (!this.emailInput.checkValidity()) {
          this.showStatus('error');
          this.emailInput.focus();
          return;
        }

        const email = this.emailInput.value.trim();
        const variantId = this.variantIdInput?.value;
        const variantTitle = this.variantTitleInput?.value;

        const properties = {
          ProductName: this.dataset.productTitle,
          ProductID: this.dataset.productId,
          ProductURL: window.location.origin + this.dataset.productUrl,
          VariantID: variantId,
          VariantTitle: variantTitle,
        };

        this.submitBtn?.setAttribute('disabled', 'disabled');
        try {
          // Rides on the Klaviyo onsite-embed script already installed via the
          // Klaviyo Shopify app — no API key handling needed here.
          if (window.klaviyo && typeof window.klaviyo.identify === 'function' && typeof window.klaviyo.track === 'function') {
            // Klaviyo's onsite SDK is already loaded: call its real methods directly.
            // identify() is async — await it so track() doesn't fire against a
            // stale identity if the shopper submits a different email than last time.
            await window.klaviyo.identify({ email });
            window.klaviyo.track('Back in Stock Request', properties);
          } else {
            // Fallback queue pattern, in case the onsite script hasn't finished loading yet.
            window.klaviyo = window.klaviyo || [];
            window.klaviyo.push(['identify', { email }]);
            window.klaviyo.push(['track', 'Back in Stock Request', properties]);
          }

          this.subscribeToKlaviyoList(email);

          this.showStatus('success');
          this.emailInput.value = '';
          // Long enough to read "You're on the list" before the popup closes.
          setTimeout(() => this.closest('modal-dialog')?.hide(), 4000);
        } catch (e) {
          console.error('[NotifyMeForm] Klaviyo push failed:', e);
          this.showStatus('error', 'Something went wrong. Please try again.');
        } finally {
          this.submitBtn?.removeAttribute('disabled');
        }
      }
    }
  );
}
